'use client';

import { useState } from 'react';
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  Loader2,
  Search,
} from 'lucide-react';

/**
 * Multi-site serviceability.
 *
 * Every competitor on this SERP — BroadbandNow, Allconnect, inmyarea, the FCC's
 * own map — checks one address. A operator with twelve branches runs the same
 * lookup twelve times and builds the comparison in a spreadsheet. That operator
 * is worth roughly twelve times a single-site lead to us, because commission is
 * a percentage of MRC.
 *
 * So this is the differentiator, and it is also the qualification step: there is
 * no search demand for "multi location business internet", so the only way to
 * find these buyers is to catch them in a funnel fed by single-site queries and
 * ask how many sites they have before asking for anything else.
 */

const MAX_SITES = 12;

interface SiteProvider {
  name: string;
  /** Absent for carriers that trade under their filing name (e.g. Bluebird). */
  brand?: string;
  bestKind: string;
  quotable: boolean;
}

interface SiteResult {
  input: string;
  status: 'ok' | 'not_found' | 'error';
  matchedAddress?: string;
  providers?: SiteProvider[];
  message?: string;
}

interface BulkResult {
  dataVintage: string;
  siteCount: number;
  resolvedCount: number;
  sites: SiteResult[];
}

const KIND_LABEL: Record<string, string> = {
  fiber: 'Fiber',
  cable: 'Cable',
  copper: 'Copper',
  wireless: 'Fixed wireless',
  satellite: 'Satellite',
  other: 'Other',
};

const KIND_DOT: Record<string, string> = {
  fiber: 'bg-emerald-500',
  cable: 'bg-blue-500',
  copper: 'bg-amber-500',
  wireless: 'bg-purple-500',
  satellite: 'bg-gray-400',
  other: 'bg-gray-400',
};

export default function MultiSiteCheck() {
  const [raw, setRaw] = useState('');
  const [lookupState, setLookupState] = useState<'idle' | 'loading' | 'done' | 'error'>(
    'idle'
  );
  const [lookupError, setLookupError] = useState('');
  const [result, setResult] = useState<BulkResult | null>(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    contractEnd: '',
    message: '',
  });
  const [honeypot, setHoneypot] = useState('');
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>(
    'idle'
  );
  const [formError, setFormError] = useState('');

  const lines = raw
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const inputClass =
    'w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 transition-colors';
  const labelClass =
    'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  async function runLookup(e: React.FormEvent) {
    e.preventDefault();
    if (lines.length === 0) {
      setLookupError('Add at least one full street address, one per line.');
      setLookupState('error');
      return;
    }
    setLookupState('loading');
    setLookupError('');

    try {
      const res = await fetch('/api/network-availability/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ addresses: lines.slice(0, MAX_SITES) }),
      });
      const data = (await res.json()) as BulkResult & { error?: string };
      if (!res.ok) {
        setLookupError(data.error || 'That lookup did not work.');
        setLookupState('error');
        return;
      }
      setResult(data);
      setLookupState('done');
    } catch {
      setLookupError(
        'We could not reach the lookup service. Send us the list below and we will run it manually.'
      );
      setLookupState('error');
    }
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault();
    if (honeypot) return;
    setFormState('submitting');
    setFormError('');

    const siteLines = result?.sites.map((s) => s.matchedAddress || s.input) ?? lines;

    try {
      const res = await fetch('/api/network-opportunity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          company: form.company,
          phone: form.phone,
          // The first site anchors the registration; the rest ride along so a
          // single Loc8 conversation covers every address.
          serviceAddress: siteLines[0],
          additionalSites: siteLines.slice(1).join('\n'),
          contractEnd: form.contractEnd,
          interests: ['internet', 'multi-site'],
          message: form.message,
          providers: result?.sites
            .flatMap((s) => s.providers ?? [])
            .map((p) => ({ name: p.name, bestKind: p.bestKind })),
          sourcePage: '/business-internet-check#multi-site',
        }),
      });
      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        setFormError(data.error || 'That did not send. Try again in a moment.');
        setFormState('error');
        return;
      }
      setFormState('success');
    } catch {
      setFormError('That did not send. Try again in a moment.');
      setFormState('error');
    }
  }

  if (formState === 'success') {
    return (
      <div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 p-8 text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-600 dark:text-emerald-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          We&apos;re on it
        </h2>
        <p className="text-gray-700 dark:text-gray-300 max-w-lg mx-auto">
          We&apos;ll run every site through the carrier serviceability tools and come
          back with what each address can actually get, with real pricing — usually
          within one business day. One conversation, not one per location.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* ---- Step 1: the list ---- */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-6 md:p-8">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
          Check every location at once
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          One address per line, up to {MAX_SITES}. Checking twelve sites takes us no
          longer than checking one — and it is the same single conversation afterwards.
        </p>

        <form onSubmit={runLookup} className="space-y-3">
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={6}
            aria-label="Business street addresses, one per line"
            placeholder={
              '1200 Main St, Kansas City, MO 64105\n450 W 33rd St, New York, NY 10001\n800 Boylston St, Boston, MA 02199'
            }
            className={inputClass}
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {lines.length === 0
                ? 'No addresses yet'
                : `${lines.length} address${lines.length === 1 ? '' : 'es'}${
                    lines.length > MAX_SITES
                      ? ` — we'll check the first ${MAX_SITES}`
                      : ''
                  }`}
            </span>
            <button
              type="submit"
              disabled={lookupState === 'loading'}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#2813e8] hover:bg-[#1f0fb8] disabled:opacity-60 text-white font-semibold transition-colors"
            >
              {lookupState === 'loading' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              Check all sites
            </button>
          </div>
        </form>

        {lookupState === 'error' && (
          <div className="mt-4 flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{lookupError}</span>
          </div>
        )}
      </section>

      {/* ---- Step 2: the matrix ---- */}
      {lookupState === 'done' && result && (
        <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 overflow-hidden">
          <div className="p-6 md:p-8 pb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
              What&apos;s reported at each site
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {result.resolvedCount} of {result.siteCount} addresses matched. Source:{' '}
              {result.dataVintage} — self-reported by carriers at census-block level, so
              treat it as a starting point. We verify each site against live carrier
              tools before quoting.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <th className="text-left font-semibold text-gray-700 dark:text-gray-300 px-6 py-3">
                    Site
                  </th>
                  <th className="text-left font-semibold text-gray-700 dark:text-gray-300 px-6 py-3">
                    Reported providers
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.sites.map((site, i) => (
                  <tr
                    key={`${site.input}-${i}`}
                    className="border-b border-gray-100 dark:border-gray-800 last:border-0 align-top"
                  >
                    <td className="px-6 py-4 min-w-[220px]">
                      <div className="flex items-start gap-2">
                        <Building2 className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {site.matchedAddress || site.input}
                          </div>
                          {site.matchedAddress &&
                            site.matchedAddress !== site.input && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                you entered: {site.input}
                              </div>
                            )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {site.status !== 'ok' ? (
                        <span className="text-amber-700 dark:text-amber-400">
                          {site.message}{' '}
                          <span className="text-gray-500 dark:text-gray-400">
                            We can still source this location.
                          </span>
                        </span>
                      ) : site.providers && site.providers.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {site.providers.map((p) => (
                            <span
                              key={p.name}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                            >
                              <span
                                className={`w-2 h-2 rounded-full ${
                                  KIND_DOT[p.bestKind] || KIND_DOT.other
                                }`}
                                aria-hidden="true"
                              />
                              {p.brand || p.name}
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {KIND_LABEL[p.bestKind] || KIND_LABEL.other}
                              </span>
                              {p.quotable && (
                                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                                  we quote
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">
                          Nothing reported in this block. That usually means a dedicated
                          circuit rather than no service — worth checking properly.
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ---- Step 3: the lead ---- */}
      {lookupState === 'done' && (
        <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-6 md:p-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            Get real pricing for all {result?.siteCount ?? lines.length} sites
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            We go to every carrier that can serve each building and come back with one
            comparison. It costs you nothing — carriers fund the channel out of the same
            budget as their own sales team.
          </p>

          <form onSubmit={submitForm} className="space-y-5">
            <input
              type="text"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="ms-firstName" className={labelClass}>
                  First name
                </label>
                <input
                  id="ms-firstName"
                  required
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="ms-lastName" className={labelClass}>
                  Last name
                </label>
                <input
                  id="ms-lastName"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="ms-email" className={labelClass}>
                  Work email
                </label>
                <input
                  id="ms-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="ms-company" className={labelClass}>
                  Company
                </label>
                <input
                  id="ms-company"
                  required
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="ms-phone" className={labelClass}>
                  Phone <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  id="ms-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="ms-contractEnd" className={labelClass}>
                  When do the current contracts end?
                </label>
                <input
                  id="ms-contractEnd"
                  value={form.contractEnd}
                  onChange={(e) => setForm({ ...form, contractEnd: e.target.value })}
                  placeholder="e.g. March 2027, staggered, or not sure"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label htmlFor="ms-message" className={labelClass}>
                Anything specific about these sites?{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                id="ms-message"
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Opening two more locations in Q1, need SD-WAN across all sites, one branch keeps dropping…"
                className={inputClass}
              />
            </div>

            {formState === 'error' && (
              <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={formState === 'submitting'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-lg bg-[#2813e8] hover:bg-[#1f0fb8] disabled:opacity-60 text-white font-semibold transition-colors"
            >
              {formState === 'submitting' && (
                <Loader2 className="w-5 h-5 animate-spin" />
              )}
              Get pricing for every site
            </button>
          </form>
        </section>
      )}
    </div>
  );
}
